import React, { forwardRef } from 'react';
import { motion } from 'framer-motion';
import { ChevronDown } from 'lucide-react';

const Select = forwardRef(({
  label,
  value,
  onChange,
  onBlur,
  options = [],
  placeholder = 'Select an option',
  error,
  helperText,
  required = false,
  disabled = false,
  className = '',
  size = 'medium',
  ...props
}, ref) => {
  const sizes = {
    small: 'px-3 py-2 text-sm',
    medium: 'px-4 py-3 text-base',
    large: 'px-5 py-4 text-lg'
  };

  const selectClasses = `
    w-full
    ${sizes[size]}
    pr-10
    border rounded-xl
    appearance-none
    bg-white/70 backdrop-blur-sm
    transition-all duration-300 ease-out
    focus:outline-none focus:ring-2 focus:ring-offset-2
    hover:bg-white/90 hover:border-pragyavan-gold/40
    disabled:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60
    cursor-pointer
    ${error
      ? 'border-red-400 focus:border-red-500 focus:ring-red-500/20 bg-red-50/30'
      : 'border-warm-gray/20 focus:border-pragyavan-gold focus:ring-pragyavan-gold/20 focus:bg-white'
    }
    ${className}
  `.trim();

  return (
    <div className="w-full">
      {label && (
        <label className="block text-sm font-medium text-pragyavan-brown mb-2">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      
      <div className="relative">
        <motion.select
          ref={ref}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          disabled={disabled}
          className={selectClasses}
          whileFocus={{ scale: 1.01 }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
          {...props}
        >
          <option value="">{placeholder}</option>
          {options.map((option) => (
            <option 
              key={option.value} 
              value={option.value}
              disabled={option.disabled}
            >
              {option.label}
            </option>
          ))}
        </motion.select>
        
        <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
          <div className="w-6 h-6 rounded-full bg-pragyavan-gold/10 flex items-center justify-center">
            <ChevronDown className="h-4 w-4 text-pragyavan-brown/60" />
          </div>
        </div>
      </div>
      
      {(error || helperText) && (
        <motion.div
          initial={{ opacity: 0, y: -5 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-2"
        >
          {error && (
            <p className="text-sm text-red-600">{error}</p>
          )}
          {!error && helperText && (
            <p className="text-sm text-warm-gray">{helperText}</p>
          )}
        </motion.div>
      )}
    </div>
  );
});

Select.displayName = 'Select';

export default Select;